import { useEffect, useState } from "react";
import {
    getCandidateEmploymentRequisites,
    regenerateCandidateContractsFromRequisites,
    updateCandidateEmploymentRequisites,
} from "../../services/candidateDocumentsApi";
import { useAlertContext } from "../../context/AlertContext";
import {
    digitsOnly,
    REQUISITE_FIELD_RULES,
    validateEmploymentRequisites,
} from "../../utils/employmentRequisites";

export { REQUISITE_FIELD_RULES, validateEmploymentRequisites };

const EMPTY = {
    full_name: "",
    birth_date: "",
    passport_series: "",
    passport_number: "",
    passport_issue_date: "",
    passport_issued_by: "",
    passport_department_code: "",
    registration_address: "",
    actual_residence_address: "",
    snils_number: "",
    inn_number: "",
    bank_account: "",
    bank_name: "",
    bank_bik: "",
    correspondent_account: "",
};

const NUMERIC_FIELDS = Object.entries(REQUISITE_FIELD_RULES)
    .filter(([, rule]) => rule.digits)
    .map(([name]) => name);

function editableForm(data, fallbackName = "") {
    const result = Object.fromEntries(
        Object.keys(EMPTY).map((name) => [name, data?.[name] ?? EMPTY[name]]),
    );
    result.full_name = data?.full_name || fallbackName || "";
    NUMERIC_FIELDS.forEach((name) => { result[name] = digitsOnly(result[name]); });
    return result;
}

const groups = [
    {
        title: "Личные данные",
        fields: [
            ["full_name", "Ф.И.О."],
            ["birth_date", "Дата рождения", "date"],
            ["snils_number", "СНИЛС"],
            ["inn_number", "ИНН"],
        ],
    },
    {
        title: "Паспорт и регистрация",
        fields: [
            ["passport_series", "Серия паспорта"],
            ["passport_number", "Номер паспорта"],
            ["passport_issue_date", "Дата выдачи", "date"],
            ["passport_department_code", "Код подразделения"],
            ["passport_issued_by", "Кем выдан", "textarea"],
            ["registration_address", "Адрес регистрации", "textarea"],
            ["actual_residence_address", "Фактический адрес проживания", "textarea"],
        ],
    },
    {
        title: "Банковские реквизиты",
        fields: [
            ["bank_account", "Расчётный счёт"],
            ["bank_name", "Банк"],
            ["bank_bik", "БИК"],
            ["correspondent_account", "Корреспондентский счёт"],
        ],
    },
];

function Field({ name, label, type = "text", value, onChange, error }) {
    const rule = REQUISITE_FIELD_RULES[name] || {};
    const count = rule.digits ? digitsOnly(value).length : String(value || "").length;
    const limit = rule.digits || rule.maxLength;
    const common = {
        id: `requisite-${name}`,
        "aria-label": label,
        value: value || "",
        onChange: (event) => {
            const next = rule.digits
                ? digitsOnly(event.target.value).slice(0, rule.digits)
                : event.target.value;
            onChange(name, next);
        },
        ...(rule.digits ? { inputMode: "numeric", maxLength: rule.digits } : {}),
        ...(rule.maxLength ? { maxLength: rule.maxLength } : {}),
        ...(type === "date" ? { min: "1900-01-01", max: new Date().toISOString().slice(0, 10) } : {}),
        ...(error ? { "aria-invalid": true, "aria-describedby": `requisite-${name}-error` } : {}),
        className: `mt-2 w-full rounded-xl border bg-white px-4 py-3 text-base text-slate-900 shadow-sm outline-none transition focus:ring-2 ${error ? "border-red-500 focus:border-red-500 focus:ring-red-100" : "border-slate-300 focus:border-amber-500 focus:ring-amber-200"}`,
    };
    return (
        <label htmlFor={common.id} className={type === "textarea" ? "md:col-span-2 block" : "block"}>
            <span className="flex items-center justify-between gap-3 text-sm font-semibold text-slate-700">
                <span>{label}</span>
                {limit ? <span className={error ? "text-red-600" : "font-normal text-slate-500"}>{count}/{limit} {rule.digits ? "цифр" : "символов"}</span> : null}
            </span>
            {type === "textarea" ? <textarea {...common} rows={3} /> : <input {...common} type={type} />}
            {error ? <span id={`requisite-${name}-error`} className="mt-1 block text-sm font-medium text-red-600">{error}</span> : null}
        </label>
    );
}

export default function CandidateEmploymentRequisites({
    candidate,
    allowRegenerate = true,
    onSaved,
}) {
    const [form, setForm] = useState(editableForm(null, candidate?.full_name));
    const [meta, setMeta] = useState({ available: false, is_verified: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { showAlert } = useAlertContext();

    useEffect(() => {
        let active = true;
        setLoading(true);
        getCandidateEmploymentRequisites(candidate.id)
            .then((data) => {
                if (!active) return;
                setForm(editableForm(data, candidate?.full_name));
                setMeta(data || { available: false, is_verified: false });
            })
            .catch((error) => showAlert(`Не удалось загрузить реквизиты: ${error?.message || error}`, "error"))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [candidate.id, candidate.full_name, showAlert]);

    const change = (name, value) => {
        setForm((current) => ({ ...current, [name]: value }));
        setErrors((current) => {
            if (!current[name]) return current;
            const next = { ...current };
            delete next[name];
            return next;
        });
    };

    const save = async (regenerate) => {
        const validationErrors = validateEmploymentRequisites(form);
        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            showAlert(Object.values(validationErrors)[0], "error");
            return;
        }
        setSaving(true);
        try {
            const saved = await updateCandidateEmploymentRequisites(candidate.id, form);
            setForm(editableForm(saved));
            setMeta(saved);
            setErrors({});
            if (regenerate) {
                try {
                    await regenerateCandidateContractsFromRequisites(candidate.id);
                    showAlert("Реквизиты сохранены, договоры поставлены на повторную генерацию", "success");
                } catch (error) {
                    showAlert(
                        `Реквизиты сохранены, но договоры не поставлены в очередь: ${error?.message || error}`,
                        "error",
                    );
                }
            } else {
                showAlert("Реквизиты сохранены и подтверждены", "success");
            }
            onSaved?.(saved);
        } catch (error) {
            showAlert(`Не удалось сохранить реквизиты: ${error?.message || error}`, "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500">Загрузка реквизитов…</div>;

    return (
        <section className="space-y-5" aria-label="Реквизиты кандидата">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-bold text-slate-900">Единые реквизиты для договоров</h2>
                        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
                            Проверьте распознанные данные один раз. После сохранения все документы заполняются из этой карточки, без служебных таблиц и повторного OCR.
                        </p>
                    </div>
                    <span className={`rounded-full px-3 py-1.5 text-sm font-semibold ${meta.is_verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-200 text-amber-900"}`}>
                        {meta.is_verified ? "Проверено HR" : "Требуется проверка"}
                    </span>
                </div>
                {!meta.available ? (
                    <p className="mt-3 text-sm text-slate-600">
                        Распознанных реквизитов пока нет. Они появятся после обработки пакета документов; при необходимости данные можно заполнить вручную.
                    </p>
                ) : null}
            </div>

            {groups.map((group) => (
                <div key={group.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-base font-bold text-slate-900">{group.title}</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {group.fields.map(([name, label, type]) => (
                            <Field key={name} name={name} label={label} type={type} value={form[name]} onChange={change} error={errors[name]} />
                        ))}
                    </div>
                    {group.title === "Банковские реквизиты" ? <p className="mt-4 rounded-xl bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
                        Чтобы заменить банковский документ ручными данными, заполните четыре поля. Расчётный и корреспондентский счета содержат по 20 цифр, БИК — 9 цифр. Ф.И.О. берётся из проверенных данных кандидата.
                    </p> : null}
                </div>
            ))}

            <div className="sticky bottom-3 flex flex-wrap justify-end gap-3 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
                <button type="button" disabled={saving} onClick={() => save(false)} className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    {saving ? "Сохранение…" : "Сохранить реквизиты"}
                </button>
                {allowRegenerate ? <button type="button" disabled={saving} onClick={() => save(true)} className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    Пересобрать договоры
                </button> : null}
            </div>
        </section>
    );
}
